import { useCart } from "@/context/CartContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { X, Minus, Plus } from "lucide-react";

export default function CartDrawer({ onCheckout }) {
  const { items, open, setOpen, removeItem, updateQty, subtotal } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-white text-black border-l border-black/10 w-full sm:max-w-md rounded-none p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-8 pb-4 border-b border-black/10">
          <SheetTitle className="text-2xl font-black tracking-tight uppercase">
            Your Bag [{items.length}]
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="cart-items-list">
          {items.length === 0 ? (
            <div className="py-32 text-center text-xs tracking-[0.3em] uppercase text-black/40">
              Bag is empty
            </div>
          ) : (
            <ul className="divide-y divide-black/10">
              {items.map((it) => (
                <li
                  key={`${it.product_id}-${it.size}`}
                  className="py-5 flex gap-4"
                  data-testid={`cart-item-${it.product_id}-${it.size}`}
                >
                  <div className="w-20 h-20 bg-black/5 flex items-center justify-center shrink-0">
                    <img
                      src={it.image}
                      alt={it.name}
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold uppercase tracking-tight truncate">
                          {it.name}
                        </p>
                        <p className="text-[11px] tracking-[0.2em] uppercase text-black/50 mt-0.5">
                          Size {it.size}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(it.product_id, it.size)}
                        className="text-black/40 hover:text-black"
                        data-testid={`remove-${it.product_id}-${it.size}`}
                        aria-label="Remove item"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-black/20">
                        <button
                          type="button"
                          onClick={() => updateQty(it.product_id, it.size, -1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white"
                          data-testid={`qty-decrease-${it.product_id}-${it.size}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span
                          className="w-8 text-center text-sm font-bold"
                          data-testid={`qty-value-${it.product_id}-${it.size}`}
                        >
                          {it.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQty(it.product_id, it.size, 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white"
                          data-testid={`qty-increase-${it.product_id}-${it.size}`}
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <span className="text-sm font-bold">
                        ${(it.price * it.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <SheetFooter className="border-t border-black/10 px-6 py-6 flex-col gap-4 sm:flex-col">
          <div className="w-full flex justify-between items-center">
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-black/60">
              Subtotal
            </span>
            <span
              className="text-xl font-black tracking-tight"
              data-testid="cart-subtotal"
            >
              ${subtotal.toFixed(2)}
            </span>
          </div>
          <button
            type="button"
            disabled={items.length === 0}
            onClick={() => {
              setOpen(false);
              onCheckout();
            }}
            className="meao-btn w-full"
            data-testid="checkout-btn"
          >
            Checkout
          </button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
